import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../context/AppContext';
import { FaArrowLeft, FaTrophy, FaMedal, FaUsers } from '../../components/Icons';
import { FaEuroSign } from 'react-icons/fa';
import { SectionHeaderComp } from '../ligas/LastresLigaPage';
import { Carrera, ResultadoCarrera, Socio } from '../../models/types';
import EquipoEstadisticasDashboard, { PilotoCarreraPuntos } from './EquipoEstadisticasDashboard';
import { sortRacesByName } from '../../utils/sortRacesByName';

// Media queries para responsive
const breakpoints = {
  mobile: '768px',
  tablet: '1024px'
};

// Componentes estilizados
const Container = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 8px;
    margin: 0;
    max-width: 100%;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  
  @media (max-width: ${breakpoints.mobile}) {
    flex-direction: column;
    gap: 16px;
    margin-bottom: 16px;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  color: #1a1a2e;
  margin: 0;
  
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 22px;
    text-align: center;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: #0f3460;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #0a2640;
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    width: 100%;
    justify-content: center;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 16px;
    margin-bottom: 16px;
  }
`;

const CardTitle = styled.h3`
  color: #0f3460;
  margin-top: 0;
  margin-bottom: 15px;
  font-size: 1.2rem;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
  
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 1rem;
    margin-bottom: 12px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;
  
  @media (max-width: ${breakpoints.mobile}) {
    min-width: 100%;
    font-size: 12px;
  }
`;

const Th = styled.th`
  background-color: #0f3460;
  color: white;
  text-align: left;
  padding: 12px 15px;
  position: sticky;
  top: 0;
  z-index: 10;
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 8px 6px;
    font-size: 11px;
  }
`;

const ThCarrera = styled(Th)`
  min-width: 120px;
  text-align: center;
  white-space: pre-line;
  line-height: 1.2;
  
  /* Add a smaller font size for the circuit name */
  & span.circuit-name {
    display: block;
    font-size: 0.85em;
    margin-top: 4px;
    font-weight: normal;
    opacity: 0.9;
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    min-width: 80px;
    font-size: 10px;
    line-height: 1.1;
    
    & span.circuit-name {
      font-size: 0.8em;
      margin-top: 2px;
    }
  }
`;

const ThPuntos = styled(Th)`
  min-width: 120px;
  text-align: center;
  background-color: #e94560;
  
  @media (max-width: ${breakpoints.mobile}) {
    min-width: 80px;
  }
`;

const Td = styled.td`
  padding: 12px 15px;
  border-bottom: 1px solid #ddd;
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 8px 6px;
    font-size: 11px;
  }
`;

const TdPuntos = styled(Td)`
  text-align: center;
  font-weight: bold;
`;

const TdCarrera = styled(Td)`
  text-align: center;
`;

const TdPiloto = styled(Td)`
  font-weight: bold;
  
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 10px;
  }
`;

const TdTotal = styled(Td)`
  text-align: center;
  font-weight: bold;
  background-color: #f8f9fa;
`;

const TdTotalDescarte = styled(Td)`
  text-align: center;
  font-weight: bold;
  background-color: #e2f0ff;
  color: #0f3460;
`;

const PosicionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Posicion = styled.div<{ pos: number }>`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 10px;
  
  @media (max-width: ${breakpoints.mobile}) {
    width: 24px;
    height: 24px;
    font-size: 12px;
    margin-right: 6px;
  }
  
  ${props => {
    if (props.pos === 1) {
      return `
        background-color: gold;
        color: #333;
      `;
    } else if (props.pos === 2) {
      return `
        background-color: silver;
        color: #333;
      `;
    } else if (props.pos === 3) {
      return `
        background-color: #cd7f32;
        color: white;
      `;
    }
  }}
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 40px;
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 24px;
  }
`;

const InfoMessage = styled.div`
  background-color: #e2f0ff;
  border-left: 4px solid #0f3460;
  padding: 15px;
  margin-bottom: 20px;
  border-radius: 4px;
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 12px;
    font-size: 14px;
    margin-bottom: 16px;
  }
`;

// Nuevos componentes responsive para las tarjetas de equipos
const EquipoCard = styled.div<{ borderColor: string }>`
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  overflow: hidden;
  border: 1px solid ${props => props.borderColor};
  border-left: 8px solid ${props => props.borderColor};
  
  @media (max-width: ${breakpoints.mobile}) {
    border-left: 4px solid ${props => props.borderColor};
  }
`;

const EquipoCardContent = styled.div`
  display: flex;
  flex-direction: row;
  
  @media (max-width: ${breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const EquipoInfoSection = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  width: 300px;
  border-right: 1px solid #f0f0f0;
  
  @media (max-width: ${breakpoints.mobile}) {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #f0f0f0;
    padding: 16px;
  }
`;

const EquipoHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  
  @media (max-width: ${breakpoints.mobile}) {
    margin-bottom: 12px;
  }
`;

const PosicionBadge = styled.div<{ backgroundColor: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${props => props.backgroundColor};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 20px;
  margin-right: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  
  @media (max-width: ${breakpoints.mobile}) {
    width: 32px;
    height: 32px;
    font-size: 16px;
    margin-right: 12px;
  }
`;

const EquipoNombre = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: bold;
  color: #1a1a2e;
  
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 18px;
  }
`;

const CocheImagen = styled.div`
  width: 100%;
  height: 120px;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 16px;
  background-color: #f5f6fa;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #eee;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  
  @media (max-width: ${breakpoints.mobile}) {
    height: 80px;
    margin-bottom: 12px;
  }
`;

const PuntuacionTotal = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  background-color: #f5f6fa;
  padding: 8px 12px;
  border-radius: 8px;
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 6px 10px;
    font-size: 14px;
  }
`;

const PuntuacionDescarte = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f0f4f8;
  padding: 8px 12px;
  border-radius: 8px;
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 6px 10px;
    font-size: 14px;
  }
`;

const ValorMercadoContainer = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: linear-gradient(135deg, #0f3460 0%, #e4312e 100%);
  border-radius: 8px;
  color: white;
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 10px;
    margin-top: 10px;
  }
`;

const ValorMercadoTitle = styled.div`
  font-weight: bold;
  font-size: 12px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  svg {
    margin-right: 6px;
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 11px;
    margin-bottom: 6px;
  }
`;

const ValorMercadoTotal = styled.div`
  font-size: 18px;
  font-weight: bold;
  text-align: center;
  margin-bottom: 8px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 16px;
    margin-bottom: 6px;
  }
`;

const ValorMercadoDesglose = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
  
  @media (max-width: ${breakpoints.mobile}) {
    gap: 3px;
    margin-top: 6px;
  }
`;

const PilotoValorItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 8px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  font-size: 12px;
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 3px 6px;
    font-size: 11px;
  }
`;

const PilotoNombre = styled.span`
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
`;

const PilotoValor = styled.span`
  color: white;
  font-weight: bold;
`;

const PilotosCarrerasSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const PilotosHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid #f0f0f0;
  background-color: #fafafa;
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 16px;
  }
`;

const PilotosGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  
  @media (max-width: ${breakpoints.mobile}) {
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }
`;

const PilotoChip = styled.a`
  text-decoration: none;
  color: inherit;
  display: flex;
  align-items: center;
  background-color: #fff;
  padding: 8px 12px;
  border-radius: 50px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  border: 1px solid #eee;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 6px 10px;
    font-size: 12px;
  }
`;

const CarrerasGrid = styled.div`
  padding: 24px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 16px;
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const CarreraCard = styled.div<{ isDescartada: boolean }>`
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #e0e0e0;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
  
  ${props => props.isDescartada && `
    background-color: #ffe6e6;
    border-color: #dc3545;
  `}
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 12px;
  }
`;

const PodioContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 20px;
  margin-bottom: 40px;
  padding: 40px 20px;
  background: linear-gradient(135deg, #0f3460 0%, #1e2340 100%);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.1);
  
  @media (max-width: ${breakpoints.mobile}) {
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 24px 16px;
    margin-bottom: 24px;
  }
`;

const PodioPosition = styled.div<{ height: string; position: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  color: white;
  min-height: ${props => props.height};
  justify-content: flex-end;
  
  @media (max-width: ${breakpoints.mobile}) {
    min-height: auto;
    width: 100%;
    ${props => props.position === 1 && 'order: -1;'}
  }
`;

const EstadisticasContainer = styled.div`
  display: block;
  
  @media (max-width: ${breakpoints.mobile}) {
    display: none;
  }
`;

// Interfaz para los resultados de un piloto en una carrera
interface PuntuacionPiloto {
  pilotoId: string;
  nombre: string;
  puntos: number;
  posicion: number;
  // Campos adicionales para desglose de puntos
  puntosBase?: number;
  puntosExtras?: number;
  puntosSanciones?: number;
}

interface ResultadoEquipo {
  equipoId: string;
  nombre: string;
  pilotos: string[];
  resultadosPorCarrera: Map<string, number>;
  pilotosPorCarrera: Map<string, PuntuacionPiloto[]>; // Nuevo campo para almacenar los pilotos que cuentan
  puntosTotales: number;
  puntosTotalesConDescarte: number;
  peorResultado: {
    carreraId: string;
    puntos: number;
  } | null;
}

// Función para formatear fechas
const formatearFecha = (fechaStr: string) => {
  if (!fechaStr) return 'Fecha no disponible';
  
  try {
    const fecha = new Date(fechaStr);
    return fecha.toLocaleDateString('es-ES', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  } catch (error) {
    console.error('Error al formatear fecha:', error);
    return fechaStr;
  }
};

const ClasificacionEquipos: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data } = useContext(AppContext);
  const [liga, setLiga] = useState<any>(null);
  const [carreras, setCarreras] = useState<Carrera[]>([]);
  const [resultadosEquipos, setResultadosEquipos] = useState<ResultadoEquipo[]>([]);
  
  useEffect(() => {
    // Buscar la liga
    const ligaEncontrada = data.ligas.find(l => l.id === id);
    if (ligaEncontrada) {
      setLiga(ligaEncontrada);
      
      // Obtener todas las carreras de la liga (solo las publicadas)
      const carrerasLiga = data.carreras.filter(c => c.ligaId === id && c.publicada !== false);
      console.log(`DEBUG - ClasificacionEquipos - Encontradas ${carrerasLiga.length} carreras publicadas para la liga ${id}`);
      
      // Ordenar carreras por nombre
      const carrerasOrdenadas = sortRacesByName([...carrerasLiga]);
      setCarreras(carrerasOrdenadas);
      
      // Obtener todos los pilotos de la liga usando el campo categoria de la liga
      let categoriaLiga = ligaEncontrada.categoria || '';
      
      // Si no hay categoria definida, intentar detectarla del nombre (para ligas antiguas)
      if (!categoriaLiga) {
        if (ligaEncontrada.nombre === 'PRO' || ligaEncontrada.nombre.includes('PRO')) {
          categoriaLiga = 'PRO';
        } else if (ligaEncontrada.nombre === 'ADV' || ligaEncontrada.nombre.includes('ADV')) {
          categoriaLiga = 'ADV';
        } else if (ligaEncontrada.nombre === 'NOV' || ligaEncontrada.nombre.includes('NOV')) {
          categoriaLiga = 'NOV';
        }
      }
      
      console.log(`DEBUG - ClasificacionEquipos - Usando categoría: ${categoriaLiga} para la liga ${ligaEncontrada.nombre}`);
      
      const pilotosLiga = data.socios.filter(socio => {
        const categoriaUpper = (socio.Categoria || '').trim().toUpperCase();
        const categoriaLigaUpper = categoriaLiga.trim().toUpperCase();
        return categoriaUpper === categoriaLigaUpper;
      });
      
      console.log(`DEBUG - ClasificacionEquipos - Encontrados ${pilotosLiga.length} pilotos para la liga ${ligaEncontrada.nombre} (categoría: ${categoriaLiga})`);
      
      // Agrupar pilotos por equipo y obtener los IDs reales de Firestore
      const equipos = new Map<string, { id: string, nombre: string, pilotos: string[] }>();
      
      // Primero, crear un mapa de nombres de equipo a IDs de Firestore
      const equiposFirestore = new Map<string, string>();
      if (data.equipos && data.equipos.length > 0) {
        data.equipos.forEach(equipo => {
          if (equipo.nombre && equipo.id) {
            equiposFirestore.set(equipo.nombre, equipo.id);
          }
        });
      }
      
      console.log('DEBUG - ClasificacionEquipos - Mapa de nombres a IDs de equipos:', equiposFirestore);
      
      pilotosLiga.forEach(piloto => {
        if (piloto.EQUIPO) {
          // Obtener el ID real del equipo en Firestore
          const equipoId = equiposFirestore.get(piloto.EQUIPO) || piloto.EQUIPO;
          
          if (!equipos.has(piloto.EQUIPO)) {
            equipos.set(piloto.EQUIPO, { 
              id: equipoId, // Usar el ID real de Firestore
              nombre: piloto.EQUIPO, 
              pilotos: [] 
            });
          }
          equipos.get(piloto.EQUIPO)!.pilotos.push(piloto.id);
        }
      });
      
      console.log(`DEBUG - ClasificacionEquipos - Encontrados ${equipos.size} equipos`);
      console.log('DEBUG - ClasificacionEquipos - Equipos con IDs:', equipos);
      
      // Calcular resultados para cada equipo
      const resultados: ResultadoEquipo[] = Array.from(equipos.entries()).map(([nombreEquipo, equipo]) => {
        // Crear mapa de resultados por carrera
        const resultadosPorCarrera = new Map<string, number>();
        
        // Inicializar con 0 para todas las carreras
        carrerasOrdenadas.forEach(carrera => {
          resultadosPorCarrera.set(carrera.id, 0);
        });
        
        // Mapa para almacenar los pilotos que cuentan en cada carrera
        const pilotosPorCarrera = new Map<string, PuntuacionPiloto[]>();
        
        // Sumar puntos de los mejores N pilotos del equipo para cada carrera
        carrerasOrdenadas.forEach(carrera => {
          // Obtener el número de pilotos que cuentan para la puntuación del equipo
          const pilotosPorEquipo = ligaEncontrada.configuracionLastres?.configuracionEquipos?.pilotosPorEquipo || 3;
          
          // Array para almacenar los pilotos que terminaron la carrera
          const pilotosTerminados: Array<{
            pilotoId: string;
            nombre: string;
            posicion: number;
            puntosBase: number;
            puntosExtras: number;
            puntosSanciones: number;
            puntosTotales: number;
          }> = [];
          
          // Primero, obtener todos los pilotos que terminaron la carrera (sin contar DNFs)
          equipo.pilotos.forEach(pilotoId => {
            const resultadoPiloto = carrera.resultados.find(r => r.socioId === pilotoId);
            if (resultadoPiloto && !resultadoPiloto.dnf) {
              // Obtener los datos del piloto
              const piloto = data.socios.find(s => s.id === pilotoId);
              let nombrePiloto = `Piloto ${pilotoId}`; // Valor por defecto
              
              if (piloto) {
                // Usar display_name si existe, si no, combinar nombre y apellido
                nombrePiloto = piloto.display_name || 
                              (piloto.nombre ? 
                                (piloto.apellido ? `${piloto.nombre} ${piloto.apellido}` : piloto.nombre) : 
                                `Piloto ${pilotoId}`);
              }
              
              // Puntos base por posición
              const puntosBase = resultadoPiloto.puntos || 0;
              
              // Puntos adicionales por extras
              const puntosExtras = resultadoPiloto.puntosAdicionales || 0;
              
              // Puntos de sanciones
              let puntosSanciones = 0;
              const sancionesPiloto = data.sanciones.filter(sancion => 
                sancion.socioId === pilotoId && sancion.carreraId === carrera.id
              );
              sancionesPiloto.forEach(sancion => {
                puntosSanciones += sancion.puntosPenalizacion || 0;
              });
              
              // Calcular puntos totales (pueden ser negativos por sanciones)
              const puntosTotales = puntosBase + puntosExtras + puntosSanciones;
              
              pilotosTerminados.push({
                pilotoId,
                nombre: nombrePiloto,
                posicion: resultadoPiloto.posicion,
                puntosBase,
                puntosExtras,
                puntosSanciones,
                puntosTotales
              });
            }
          });
          
          // Ordenar pilotos por posición de carrera (1º, 2º, 3º...)
          pilotosTerminados.sort((a, b) => a.posicion - b.posicion);
          
          // Tomar los N primeros pilotos por posición (sin contar sanciones aún)
          const pilotosSeleccionados = pilotosTerminados.slice(0, pilotosPorEquipo);
          
          // Crear array para los pilotos que contarán para el equipo (con sanciones aplicadas)
          const pilotosConPuntos: PuntuacionPiloto[] = [];
          
          // Para cada piloto seleccionado, aplicar sanciones y calcular puntos finales
          pilotosSeleccionados.forEach(piloto => {
            // Los puntos finales ya incluyen sanciones (calculados arriba)
            const puntosFinales = Math.max(0, piloto.puntosTotales); // No permitir puntos negativos
            
            pilotosConPuntos.push({
              pilotoId: piloto.pilotoId,
              nombre: piloto.nombre,
              puntos: puntosFinales,
              posicion: piloto.posicion,
              // Guardar información adicional para mostrar en tooltips o detalles
              puntosBase: piloto.puntosBase,
              puntosExtras: piloto.puntosExtras,
              puntosSanciones: piloto.puntosSanciones
            } as PuntuacionPiloto);
          });
          
          // Almacenar los pilotos que cuentan para esta carrera
          pilotosPorCarrera.set(carrera.id, pilotosConPuntos);
          
          // Sumar los puntos de los pilotos seleccionados (después de aplicar sanciones)
          const puntosEquipoEnCarrera = pilotosConPuntos.reduce((sum, piloto) => sum + piloto.puntos, 0);
          
          // Almacenar los puntos para esta carrera
          resultadosPorCarrera.set(carrera.id, puntosEquipoEnCarrera);
        });
        
        // Calcular puntos totales
        let puntosTotales = 0;
        const resultadosConPuntos: { carreraId: string, puntos: number }[] = [];
        
        resultadosPorCarrera.forEach((puntos, carreraId) => {
          puntosTotales += puntos;
          resultadosConPuntos.push({
            carreraId,
            puntos
          });
        });
        
        // Encontrar el peor resultado (si hay al menos una carrera)
        let peorResultado: { carreraId: string, puntos: number } | null = null;
        
        if (resultadosConPuntos.length > 0) {
          // Filtrar resultados con puntos > 0 para el descarte
          const resultadosValidos = resultadosConPuntos.filter(r => r.puntos > 0);
          
          if (resultadosValidos.length > 0) {
            // Ordenar los resultados de menor a mayor puntuación
            const resultadosOrdenados = [...resultadosValidos].sort((a, b) => a.puntos - b.puntos);
            
            // El peor resultado es el primero (menor puntuación)
            peorResultado = resultadosOrdenados[0];
            
            console.log(`DEBUG - Peor resultado para equipo ${equipo.nombre}: ${peorResultado.puntos} puntos en carrera ${peorResultado.carreraId}`);
          }
        }
        
        // Calcular puntos totales con descarte
        let puntosTotalesConDescarte = puntosTotales;
        
        // Solo aplicar el descarte si hay más de una carrera y el peor resultado existe
        if (peorResultado && carrerasOrdenadas.length > 1) {
          // Si el peor resultado tiene puntos (no es una carrera futura con 0 puntos)
          // o si todas las carreras tienen resultados, aplicar el descarte
          const totalCarrerasConResultados = resultadosConPuntos.filter(r => r.puntos > 0).length;
          
          if (peorResultado.puntos > 0 || totalCarrerasConResultados === carrerasOrdenadas.length) {
            puntosTotalesConDescarte = Math.max(0, puntosTotales - (peorResultado?.puntos || 0));
            console.log(`DEBUG - Descartando ${peorResultado.puntos} puntos para equipo ${equipo.nombre}, nuevo total: ${puntosTotalesConDescarte}`);
          } else {
            // Si el peor resultado es 0 puntos y hay carreras sin resultados,
            // no aplicar el descarte (mantener el total original)
            console.log(`DEBUG - No se descarta para equipo ${equipo.nombre} porque el peor resultado es 0 en una carrera futura`);
          }
        }
        
        return {
          equipoId: equipo.id, // Usar el ID real de Firestore
          nombre: equipo.nombre,
          pilotos: equipo.pilotos,
          resultadosPorCarrera,
          pilotosPorCarrera, // Almacenar los pilotos que cuentan en cada carrera
          puntosTotales,
          puntosTotalesConDescarte, // Ya calculado anteriormente
          peorResultado // Ya calculado anteriormente
        };
      });
      
      // Ordenar por puntos totales con descarte (de mayor a menor)
      const resultadosOrdenados = [...resultados].sort((a, b) => 
        b.puntosTotalesConDescarte - a.puntosTotalesConDescarte
      );
      
      setResultadosEquipos(resultadosOrdenados);
    }
  }, [id, data]);
  
  // Función para renderizar el nombre de la carrera formateado
  const renderizarNombreCarrera = (carrera: Carrera) => {
    return (
      <>
        <div>{carrera.nombre} ({formatearFecha(carrera.fecha)})</div>
        {carrera.circuito && <span className="circuit-name">{carrera.circuito}</span>}
      </>
    );
  };
  
  // Función para renderizar el valor de puntos con color según si es el peor resultado
  const renderizarPuntos = (equipo: ResultadoEquipo, carreraId: string) => {
    const puntos = equipo.resultadosPorCarrera.get(carreraId);
    
    if (puntos === undefined) {
      return '-';
    }
    
    const esPeorResultado = equipo.peorResultado && 
                           equipo.peorResultado.carreraId === carreraId && 
                           carreras.length > 1;
    
    return (
      <span style={{ 
        color: esPeorResultado ? '#dc3545' : 'inherit',
        textDecoration: esPeorResultado ? 'line-through' : 'none',
        opacity: esPeorResultado ? 0.7 : 1
      }}>
        {puntos}
      </span>
    );
  };
  
  // Función para obtener los nombres de los pilotos de un equipo como string
  const getNombresPilotosEquipo = (pilotosIds: string[]): string => {
    return pilotosIds
      .map(id => {
        const piloto = data.socios.find((s: Socio) => s.id === id);
        if (!piloto) return 'Piloto no encontrado';
        
        return piloto.display_name || 
               (piloto.nombre && piloto.apellido ? 
                 `${piloto.nombre} ${piloto.apellido}` : 
                 piloto.nombre || 'Sin nombre');
      })
      .join(', ');
  };
  
  // Depuración: Mostrar información sobre equipos
  useEffect(() => {
    console.log('DEBUG - Equipos disponibles:', data.equipos);
    console.log('DEBUG - Resultados equipos:', resultadosEquipos);
    
    // Verificar si los IDs coinciden
    if (data.equipos && resultadosEquipos.length > 0) {
      resultadosEquipos.forEach(resultado => {
        const equipoEncontrado = data.equipos?.find(e => e.id === resultado.equipoId);
        console.log(`DEBUG - Equipo ${resultado.nombre} (ID: ${resultado.equipoId}) - ¿Encontrado en data.equipos?: ${!!equipoEncontrado}`);
        if (equipoEncontrado) {
          console.log(`DEBUG - Imagen del coche: ${equipoEncontrado.imagenCoche || 'No disponible'}`);
        }
      });
    }
  }, [data.equipos, resultadosEquipos]);
  
  if (!liga) {
    return (
      <Container>
        <Header>
          <Title>Liga no encontrada</Title>

        </Header>
        <Card>
          <EmptyMessage>
            La liga especificada no existe.
          </EmptyMessage>
        </Card>
      </Container>
    );
  }
  
  // Función para obtener los pilotos de un equipo
  const getPilotosEquipo = (pilotosIds: string[]): Socio[] => {
    return pilotosIds
      .map(id => data.socios.find((p: Socio) => p.id === id))
      .filter((p): p is Socio => p !== undefined);
  };

  // Funciones para calcular el valor de mercado (MISMA LÓGICA QUE EN /equipos)
  const calcularEstadisticasPiloto = (socio: Socio) => {
    if (!socio || !data) return null;

    // Obtener carreras del piloto
    const carreras = data.carreras.filter(c => c.publicada && c.resultados.some(r => r.socioId === socio.id));
    const sanciones = data.sanciones?.filter(s => s.socioId === socio.id) || [];

    let carrerasCompletadas = 0;
    let victorias = 0;
    let podios = 0;
    let abandonos = 0;
    let sancionesRecibidas = sanciones.length;
    let totalPosicionesCarrera = 0;
    let carrerasConPosicionValida = 0;

    carreras.forEach((carrera) => {
      const resultado = carrera.resultados.find(r => r.socioId === socio.id);
      if (resultado) {
        carrerasCompletadas++;
        
        if (resultado.posicion === 1) victorias++;
        if (resultado.posicion && resultado.posicion <= 3) podios++;
        if (resultado.dnf) abandonos++;
        
        if (resultado.posicion && !resultado.dnf) {
          totalPosicionesCarrera += resultado.posicion;
          carrerasConPosicionValida++;
        }
      }
    });

    const posicionMediaCarrera = carrerasConPosicionValida > 0 ? totalPosicionesCarrera / carrerasConPosicionValida : 0;
    
    // Obtener posición en la liga
    const { posicionGeneral, totalPilotosEnLiga } = obtenerPosicionEnLiga(socio);
    
    // Calcular reputación
    const reputacion = calcularReputacion({
      carrerasCompletadas,
      victorias,
      podios,
      abandonos,
      sancionesRecibidas,
      posicionMediaCarrera,
      posicionGeneral,
      totalPilotosEnLiga
    });

    return { reputacion };
  };

  // Función para obtener posición en la liga
  const obtenerPosicionEnLiga = (piloto: Socio) => {
    if (!data) return { posicionGeneral: 0, totalPilotosEnLiga: 0 };

    const categoria = piloto.Categoria;
    const ligaDelPiloto = data.ligas.find(liga => categoria && liga.nombre.includes(categoria));
    
    const pilotosEnLiga = data.socios.filter(s => s.Categoria === categoria);
    
    if (pilotosEnLiga.length === 0) {
      return { posicionGeneral: 1, totalPilotosEnLiga: 1 };
    }

    // Calcular puntos de cada piloto
    const pilotosConPuntos = pilotosEnLiga.map((p: Socio) => {
      let puntosTotales = 0;
      data.carreras.forEach(carrera => {
        if (carrera.publicada) {
          const resultado = carrera.resultados.find(r => r.socioId === p.id);
          if (resultado) {
            puntosTotales += resultado.puntos || 0;
          }
        }
      });
      return { ...p, puntosTotalesCalculados: puntosTotales };
    });

    const pilotosOrdenados = [...pilotosConPuntos].sort((a: any, b: any) => {
      return (b.puntosTotalesCalculados || 0) - (a.puntosTotalesCalculados || 0);
    });

    const posicion = pilotosOrdenados.findIndex((p: any) => p.id === piloto.id) + 1;
    
    return {
      posicionGeneral: posicion > 0 ? posicion : pilotosOrdenados.length,
      totalPilotosEnLiga: pilotosOrdenados.length
    };
  };

  const calcularReputacion = (stats: {
    carrerasCompletadas: number;
    victorias: number;
    podios: number;
    abandonos: number;
    sancionesRecibidas: number;
    posicionMediaCarrera: number;
    posicionGeneral: number;
    totalPilotosEnLiga: number;
  }) => {
    if (stats.carrerasCompletadas === 0) return 50;

    // Factor de posición (40%)
    let factorPosicion = 40;
    if (stats.totalPilotosEnLiga > 1 && stats.posicionGeneral > 1) {
      const porcentajePosicion = (stats.posicionGeneral - 1) / (stats.totalPilotosEnLiga - 1);
      factorPosicion = Math.round(40 * (1 - porcentajePosicion));
    }

    // Factor de resultados (40%)
    let factorResultados = 20;
    const ratioVictorias = stats.carrerasCompletadas > 0 ? stats.victorias / stats.carrerasCompletadas : 0;
    const ratioPodiosNoVictorias = stats.carrerasCompletadas > 0 ? 
      Math.max(0, (stats.podios - stats.victorias) / stats.carrerasCompletadas) : 0;
    const ratioAbandonos = stats.carrerasCompletadas > 0 ? stats.abandonos / stats.carrerasCompletadas : 0;

    factorResultados += Math.round(ratioVictorias * 20);
    factorResultados += Math.round(ratioPodiosNoVictorias * 15);
    factorResultados -= Math.round(ratioAbandonos * 15);
    factorResultados = Math.max(0, Math.min(40, factorResultados));

    // Factor de sanciones (20%)
    let factorSanciones = 20;
    if (stats.carrerasCompletadas > 0 && stats.sancionesRecibidas > 0) {
      const ratioSanciones = stats.sancionesRecibidas / stats.carrerasCompletadas;
      factorSanciones = Math.max(0, 20 - Math.round(ratioSanciones * 15));
    }

    return Math.min(100, factorPosicion + factorResultados + factorSanciones);
  };

  // Función para calcular el valor de mercado de un piloto
  const calcularValorMercado = (socio: Socio) => {
    const stats = calcularEstadisticasPiloto(socio);
    if (!stats) return 0;
    
    const reputacion = stats.reputacion || 0;
    const sueldoBase = 84000;
    return Math.round(sueldoBase * (reputacion / 100));
  };

  // Función para calcular el valor total del equipo (usando TODOS los pilotos del equipo, no solo los de la liga)
  const calcularValorTotalEquipo = (nombreEquipo: string) => {
    // Buscar el equipo en los datos globales
    const equipoData = data.equipos?.find((e: any) => e.nombre === nombreEquipo);
    
    if (!equipoData) {
      return 0;
    }
    
    // En data.equipos los pilotos están en la propiedad 'socios', no 'pilotos'
    const pilotosIds = equipoData.socios || equipoData.pilotos || [];
    if (!pilotosIds || pilotosIds.length === 0) {
      return 0;
    }
    
    // Obtener todos los pilotos del equipo
    const pilotos = pilotosIds
      .map((pilotoId: string) => data.socios.find((s: Socio) => s.id === pilotoId))
      .filter((p: Socio | undefined): p is Socio => p !== undefined);
    
    const valorTotal = pilotos.reduce((total: number, piloto: Socio) => total + calcularValorMercado(piloto), 0);
    
    return valorTotal;
  };

  // Función para obtener el desglose de valor por piloto
  const obtenerDesglosePorPiloto = (nombreEquipo: string) => {
    // Buscar el equipo en los datos globales
    const equipoData = data.equipos?.find((e: any) => e.nombre === nombreEquipo);
    if (!equipoData) return [];
    
    // En data.equipos los pilotos están en la propiedad 'socios', no 'pilotos'
    const pilotosIds = equipoData.socios || equipoData.pilotos || [];
    if (!pilotosIds || pilotosIds.length === 0) return [];
    
    // Obtener todos los pilotos del equipo con sus valores
    const pilotos = pilotosIds
      .map((pilotoId: string) => {
        const piloto = data.socios.find((s: Socio) => s.id === pilotoId);
        if (!piloto) return null;
        
        return {
          id: piloto.id,
          nombre: piloto.display_name || (piloto.nombre && piloto.apellido ? `${piloto.nombre} ${piloto.apellido}` : piloto.nombre || 'Sin nombre'),
          valor: calcularValorMercado(piloto)
        };
      })
      .filter((p: any) => p !== null)
      .sort((a: any, b: any) => b.valor - a.valor); // Ordenar por valor descendente
    
    return pilotos;
  };

  // Función para formatear moneda EUR
  const formatearEUR = (valor: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(valor);
  };

  return (
    <Container>
      {resultadosEquipos.length >= 3 && (
        <SectionHeaderComp icon={<FaTrophy style={{ color: '#e4312e' }} />}>
          Podio de Equipos
        </SectionHeaderComp>
      )}

      {carreras.length === 0 ? (
        <Card>
          <EmptyMessage>
            No hay carreras registradas para esta liga.
          </EmptyMessage>
        </Card>
      ) : resultadosEquipos.length === 0 ? (
        <Card>
          <EmptyMessage>
            No hay equipos registrados para esta liga.
          </EmptyMessage>
        </Card>
      ) : (
        <>
          {/* Podio estilo F1 para los tres primeros equipos - RESPONSIVE */}
          {resultadosEquipos.length >= 3 && (
            <div style={{ marginBottom: '30px' }}>
              {/* Podio de Equipos title moved to SectionHeaderComp above */}
              <PodioContainer>
                {/* Segundo lugar */}
                {resultadosEquipos[1] && (
                  <PodioPosition height="140px" position={2}>
                    <div style={{ 
                      width: '180px', 
                      height: '120px',
                      position: 'relative',
                      marginBottom: '10px'
                    }}>
                      {(() => {
                        const equipo = resultadosEquipos[1];
                        if (!equipo) return null;
                        
                        // Buscar la imagen del coche del equipo
                        console.log(`DEBUG - Buscando equipo con ID: ${equipo.equipoId}, Nombre: ${equipo.nombre}`);
                        console.log(`DEBUG - Equipos disponibles:`, data.equipos);
                        const equipoData = data.equipos?.find((e: any) => e.id === equipo.equipoId);
                        console.log(`DEBUG - Equipo encontrado:`, equipoData);
                        const imagenCoche = equipoData?.imagenCoche;
                        console.log(`DEBUG - Imagen del coche:`, imagenCoche);
                        
                        return (
                          <>
                            <div style={{
                              width: '100%',
                              height: '100%',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              border: '3px solid silver',
                              boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                              background: '#f5f6fa',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {imagenCoche ? (
                                <img
                                  src={imagenCoche}
                                  alt="coche"
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain'
                                  }}
                                />
                              ) : (
                                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#666' }}>
                                  {equipo.nombre}
                                </div>
                              )}
                            </div>
                            <div style={{
                              position: 'absolute',
                              bottom: '-10px',
                              right: '-10px',
                              backgroundColor: 'silver',
                              borderRadius: '50%',
                              width: '40px',
                              height: '40px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#333',
                              fontWeight: 'bold',
                              fontSize: '20px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                            }}>2</div>
                          </>
                        );
                      })()}
                    </div>
                    <div style={{ fontWeight: 'bold', fontSize: '16px', textAlign: 'center' }}>
                      {resultadosEquipos[1]?.nombre || 'N/A'}
                    </div>
                    <div style={{ color: 'white', fontWeight: 'bold', fontSize: '18px' }}>
                      {resultadosEquipos[1]?.puntosTotales || 0} pts
                    </div>
                  </PodioPosition>
                )}
                
                {/* Primer lugar */}
                {resultadosEquipos[0] && (
                  <PodioPosition height="180px" position={1}>
                    <div style={{ 
                      width: '220px', 
                      height: '140px',
                      position: 'relative',
                      marginBottom: '10px'
                    }}>
                      {(() => {
                        const equipo = resultadosEquipos[0];
                        // Buscar la imagen del coche del equipo
                        const equipoData = data.equipos?.find(e => e.id === equipo.equipoId);
                        const imagenCoche = equipoData?.imagenCoche;
                        return (
                          <>
                            <div style={{
                              width: '100%',
                              height: '100%',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              border: '4px solid gold',
                              boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
                              background: '#f5f6fa',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {imagenCoche ? (
                                <img
                                  src={imagenCoche}
                                  alt="coche"
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain'
                                  }}
                                />
                              ) : (
                                <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#666' }}>{equipo.nombre}</div>
                              )}
                            </div>
                            <div style={{
                              position: 'absolute',
                              bottom: '-10px',
                              right: '-10px',
                              backgroundColor: 'gold',
                              borderRadius: '50%',
                              width: '50px',
                              height: '50px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#333',
                              fontWeight: 'bold',
                              fontSize: '24px',
                              boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
                            }}>1</div>
                          </>
                        );
                      })()}
                    </div>
                    <div style={{ fontWeight: 'bold', fontSize: '20px', textAlign: 'center' }}>
                      {resultadosEquipos[0]?.nombre || 'N/A'}
                    </div>
                    <div style={{ color: 'white', fontWeight: 'bold', fontSize: '22px' }}>
                      {resultadosEquipos[0]?.puntosTotales || 0} pts
                    </div>
                  </PodioPosition>
                )}
                
                {/* Tercer lugar */}
                {resultadosEquipos[2] && (
                  <PodioPosition height="120px" position={3}>
                    <div style={{ 
                      width: '150px', 
                      height: '100px',
                      position: 'relative',
                      marginBottom: '10px'
                    }}>
                      {(() => {
                        const equipo = resultadosEquipos[2];
                        if (!equipo) return null;
                        
                        // Buscar la imagen del coche del equipo
                        const equipoData = data.equipos?.find((e: any) => e.id === equipo.equipoId);
                        const imagenCoche = equipoData?.imagenCoche;
                        return (
                          <>
                            <div style={{
                              width: '100%',
                              height: '100%',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              border: '3px solid #cd7f32',
                              boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                              background: '#f5f6fa',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {imagenCoche ? (
                                <img
                                  src={imagenCoche}
                                  alt="coche"
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain'
                                  }}
                                />
                              ) : (
                                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#666' }}>{equipo.nombre}</div>
                              )}
                            </div>
                            <div style={{
                              position: 'absolute',
                              bottom: '-10px',
                              right: '-10px',
                              backgroundColor: '#cd7f32',
                              borderRadius: '50%',
                              width: '35px',
                              height: '35px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '18px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                            }}>3</div>
                          </>
                        );
                      })()}
                    </div>
                    <div style={{ fontWeight: 'bold', fontSize: '14px', textAlign: 'center' }}>
                      {resultadosEquipos[2]?.nombre || 'N/A'}
                    </div>
                    <div style={{ color: 'white', fontWeight: 'bold', fontSize: '16px' }}>
                      {resultadosEquipos[2]?.puntosTotales || 0} pts
                    </div>
                  </PodioPosition>
                )}
              </PodioContainer>
            </div>
          )}
          
          <SectionHeaderComp icon={<FaUsers style={{ color: '#e4312e' }} />}>
            Clasificación por Equipos: {liga.nombre}
          </SectionHeaderComp>
          <InfoMessage>
            Esta clasificación muestra los puntos acumulados por cada equipo, sumando los puntos de todos sus pilotos en cada carrera.
            {carreras.length > 1 && (
              <div style={{ marginTop: '10px' }}>
                La columna "Total con descarte" muestra la puntuación total descartando el peor resultado (marcado en rojo).
              </div>
            )}
          </InfoMessage>
          
          <div style={{ marginBottom: '40px' }}>
            {/* Tarjetas de equipos estilo F1 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {resultadosEquipos.map((equipo, index) => {
                // Buscar datos del equipo en Firestore
                const equipoData = data.equipos?.find(e => e.id === equipo.equipoId);
                const imagenCoche = equipoData?.imagenCoche;
                
                // Obtener pilotos del equipo
                const pilotosEquipo = equipo.pilotos.map(pilotoId => {
                  const piloto = data.socios.find(s => s.id === pilotoId);
                  return piloto ? {
                    id: pilotoId,
                    nombre: piloto.display_name || (piloto.nombre && piloto.apellido ? `${piloto.nombre} ${piloto.apellido}` : piloto.nombre || 'Sin nombre'),
                    foto: piloto.fotoUrl
                  } : null;
                }).filter(p => p !== null);
                
                // Colores según posición
                const getPositionColor = (pos: number) => {
                  if (pos === 0) return '#FFD700'; // Oro
                  if (pos === 1) return '#C0C0C0'; // Plata
                  if (pos === 2) return '#CD7F32'; // Bronce
                  return '#0F3460'; // Color base para el resto
                };
                
                return (
                  <EquipoCard key={equipo.equipoId} borderColor={getPositionColor(index)}>
                    <EquipoCardContent>
                      {/* Posición y equipo */}
                      <EquipoInfoSection>
                        <EquipoHeader>
                          <PosicionBadge backgroundColor={getPositionColor(index)}>
                            {index + 1}
                          </PosicionBadge>
                          <EquipoNombre>
                            {equipo.nombre}
                          </EquipoNombre>
                        </EquipoHeader>
                        
                        {/* Imagen del coche */}
                        <CocheImagen>
                          {imagenCoche ? (
                            <>
                              {console.log(`Mostrando imagen para equipo ${equipo.nombre}: ${imagenCoche}`)}
                              <img
                                src={imagenCoche}
                                alt={`Coche de ${equipo.nombre}`}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'contain',
                                  maxWidth: '100%'
                                }}
                                onError={(e) => {
                                  console.error(`Error cargando imagen para ${equipo.nombre}:`, e);
                                  // Cambiar a imagen de respaldo o mostrar mensaje
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            </>
                          ) : (
                            <div style={{ color: '#666', fontStyle: 'italic' }}>Sin imagen</div>
                          )}
                        </CocheImagen>
                        
                        {/* Puntuación total */}
                        <PuntuacionTotal>
                          <div style={{ fontWeight: 'bold', color: '#333' }}>PUNTOS TOTALES</div>
                          <div style={{ 
                            fontSize: '24px', 
                            fontWeight: 'bold', 
                            color: getPositionColor(index)
                          }}>
                            {equipo.puntosTotales}
                          </div>
                        </PuntuacionTotal>
                        
                        {/* Puntuación con descarte */}
                        {carreras.length > 1 && (
                          <PuntuacionDescarte>
                            <div style={{ fontWeight: 'bold', color: '#666' }}>CON DESCARTE</div>
                            <div style={{ 
                              fontSize: '20px', 
                              fontWeight: 'bold', 
                              color: '#666'
                            }}>
                              {equipo.puntosTotalesConDescarte}
                            </div>
                          </PuntuacionDescarte>
                        )}

                        {/* Valor de mercado del equipo */}
                        <ValorMercadoContainer>
                          <ValorMercadoTitle>
                            <FaEuroSign />
                            Valor de Mercado
                          </ValorMercadoTitle>
                          <ValorMercadoTotal>
                            {formatearEUR(calcularValorTotalEquipo(equipo.nombre))}
                          </ValorMercadoTotal>
                          <ValorMercadoDesglose>
                            {obtenerDesglosePorPiloto(equipo.nombre).map((piloto: any) => (
                              <PilotoValorItem key={piloto.id}>
                                <PilotoNombre>{piloto.nombre}</PilotoNombre>
                                <PilotoValor>{formatearEUR(piloto.valor)}</PilotoValor>
                              </PilotoValorItem>
                            ))}
                          </ValorMercadoDesglose>
                        </ValorMercadoContainer>
                      </EquipoInfoSection>
                      
                      {/* Pilotos y resultados */}
                      <PilotosCarrerasSection>
                        {/* Pilotos */}
                        <PilotosHeader>
                          <h3 style={{ margin: '0 0 16px 0', color: '#666', fontSize: '14px', textTransform: 'uppercase' }}>Pilotos</h3>
                          <PilotosGrid>
                            {pilotosEquipo.map(piloto => piloto && (
                              <PilotoChip 
                                key={piloto.id} 
                                href={`#`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  navigate(`/socios/profile/${piloto.id}`);
                                }}
                              >
                                <img 
                                  src={piloto.foto || `https://ui-avatars.com/api/?name=${encodeURIComponent(piloto.nombre)}&background=eee&color=666&size=64&rounded=true`}
                                  alt={piloto.nombre}
                                  style={{
                                    width: '30px',
                                    height: '30px',
                                    borderRadius: '50%',
                                    marginRight: '8px',
                                    objectFit: 'cover',
                                    border: '2px solid #fff',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                  }}
                                />
                                <span style={{ fontWeight: 'bold' }}>{piloto.nombre}</span>
                              </PilotoChip>
                            ))}
                          </PilotosGrid>
                        </PilotosHeader>
                        
                        {/* Resultados por carrera */}
                        <CarrerasGrid>
                          {carreras.map(carrera => {
                            const puntos = equipo.resultadosPorCarrera.get(carrera.id) || 0;
                            const esPeorResultado = equipo.peorResultado?.carreraId === carrera.id;
                            
                            return (
                              <CarreraCard key={carrera.id} isDescartada={esPeorResultado && carreras.length > 1}>
                                <div style={{ 
                                  fontSize: '12px', 
                                  color: '#666', 
                                  marginBottom: '8px',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  fontWeight: 'bold'
                                }}>
                                  {carrera.nombre}
                                </div>
                                <div style={{ 
                                  fontSize: '11px', 
                                  color: '#999', 
                                  marginBottom: '4px'
                                }}>
                                  {carrera.circuito || 'Sin circuito'}
                                </div>
                                <div style={{ 
                                  fontSize: '10px', 
                                  color: '#999', 
                                  marginBottom: '12px'
                                }}>
                                  {formatearFecha(carrera.fecha)}
                                </div>
                                <div style={{ 
                                  fontSize: '24px', 
                                  fontWeight: 'bold',
                                  color: (esPeorResultado && carreras.length > 1) ? '#dc3545' : '#333',
                                  textDecoration: (esPeorResultado && carreras.length > 1) ? 'line-through' : 'none',
                                  opacity: (esPeorResultado && carreras.length > 1) ? 0.7 : 1,
                                  textAlign: 'center',
                                  margin: '8px 0 12px 0'
                                }}>
                                  {puntos} pts
                                </div>
                                {esPeorResultado && carreras.length > 1 && (
                                  <div style={{ 
                                    fontSize: '10px', 
                                    color: '#dc3545', 
                                    marginTop: '4px',
                                    textAlign: 'center',
                                    fontWeight: 'bold'
                                  }}>
                                    CARRERA DESCARTADA
                                  </div>
                                )}
                                {(equipo.pilotosPorCarrera.get(carrera.id) || []).length > 0 && (
                                  <div style={{ 
                                    marginTop: '12px',
                                    borderTop: '1px solid #f0f0f0',
                                    paddingTop: '8px'
                                  }}>
                                    <div style={{
                                      fontSize: '10px',
                                      color: '#999',
                                      marginBottom: '6px',
                                      textTransform: 'uppercase',
                                      fontWeight: 'bold'
                                    }}>
                                      Pilotos:
                                    </div>
                                    {equipo.pilotosPorCarrera.get(carrera.id)?.map((piloto, idx) => (
                                      <div 
                                        key={idx} 
                                        style={{ 
                                          display: 'flex', 
                                          alignItems: 'center',
                                          marginBottom: '6px',
                                          padding: '4px 0',
                                          borderBottom: '1px solid #f8f8f8',
                                          justifyContent: 'space-between'
                                        }}
                                      >
                                        <div style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          flex: 1,
                                          minWidth: 0
                                        }}>
                                          <div style={{
                                            width: '22px',
                                            height: '22px',
                                            borderRadius: '50%',
                                            backgroundColor: '#f0f0f0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginRight: '10px',
                                            fontSize: '10px',
                                            fontWeight: 'bold',
                                            color: '#333',
                                            flexShrink: 0,
                                            border: '1px solid #ddd'
                                          }}>
                                            {piloto.posicion}º
                                          </div>
                                          <span style={{ 
                                            whiteSpace: 'nowrap', 
                                            overflow: 'hidden', 
                                            textOverflow: 'ellipsis',
                                            fontSize: '12px',
                                            fontWeight: 500,
                                            color: '#333'
                                          }}>
                                            {piloto.nombre}
                                          </span>
                                        </div>
                                        <div style={{ 
                                          fontWeight: 'bold',
                                          color: '#0f3460',
                                          marginLeft: '8px',
                                          fontSize: '12px',
                                          flexShrink: 0,
                                          whiteSpace: 'nowrap',
                                          minWidth: '50px',
                                          textAlign: 'right'
                                        }}>
                                          {piloto.puntos} pts
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </CarreraCard>
                            );
                          })}
                        </CarrerasGrid>
                      </PilotosCarrerasSection>
                    </EquipoCardContent>
                            {/* --- DASHBOARD ESTADÍSTICAS AVANZADAS --- */}
                            <EstadisticasContainer>
                              {pilotosEquipo.filter(Boolean).length > 0 && carreras.length > 0 && (
                                <EquipoEstadisticasDashboard
                                pilotos={pilotosEquipo.filter(Boolean).map(pilotoRaw => {
                                  const piloto = pilotoRaw as Socio;
                                  // Calcular los puntos y estadísticas de este piloto
                                  const puntosPorCarrera = carreras.map(c => {
                                    const resultado = c.resultados.find(r => r.socioId === piloto.id);
                                    let puntos = 0, victoria = false, podio = false, pole = false, vr = false, sanciones = 0;
                                    if (resultado) {
                                      puntos = resultado.puntos + (resultado.puntosAdicionales || 0);
                                      victoria = resultado.posicion === 1;
                                      podio = [1,2,3].includes(resultado.posicion);
                                      pole = !!resultado.pole;
                                      vr = !!resultado.vueltaRapida;
                                      // No hay sanciones en ResultadoCarrera por interfaz, así que dejamos sanciones en 0
                                    }
                                    return {
                                      carreraId: c.id,
                                      carreraNombre: c.nombre,
                                      puntos
                                    };
                                  });
                                  const total = puntosPorCarrera.reduce((a, b) => a + b.puntos, 0);
                                  const victorias = puntosPorCarrera.filter((_, idx) => {
                                    const resultado = carreras[idx].resultados.find(r => r.socioId === piloto.id);
                                    return resultado && resultado.posicion === 1;
                                  }).length;
                                  const podios = puntosPorCarrera.filter((_, idx) => {
                                    const resultado = carreras[idx].resultados.find(r => r.socioId === piloto.id);
                                    return resultado && [1,2,3].includes(resultado.posicion);
                                  }).length;
                                  const poles = puntosPorCarrera.filter((_, idx) => {
                                    const resultado = carreras[idx].resultados.find(r => r.socioId === piloto.id);
                                    return resultado && resultado.pole;
                                  }).length;
                                  const vueltasRapidas = puntosPorCarrera.filter((_, idx) => {
                                    const resultado = carreras[idx].resultados.find(r => r.socioId === piloto.id);
                                    return resultado && resultado.vueltaRapida;
                                  }).length;
                                  // Sanciones no existen en ResultadoCarrera, así que sumamos 0
                                  const sanciones = 0;
                                  // Garantizar que la foto nunca sea undefined
                                  let nombre = piloto.nombre || '';
                                  let foto = piloto.fotoPerfil || piloto.fotoUrl;
                                  if (!foto) {
                                    foto = `https://ui-avatars.com/api/?name=${encodeURIComponent(nombre)}&background=eee&color=666&size=64&rounded=true`;
                                  }
                                  return {
                                    pilotoId: piloto.id,
                                    nombre,
                                    foto,
                                    puntosPorCarrera,
                                    total,
                                    victorias,
                                    podios,
                                    poles,
                                    vueltasRapidas,
                                    sanciones
                                  };
                                })}
                                carreras={carreras}
                                equipoNombre={equipo.nombre}
                              />
                              )}
                            </EstadisticasContainer>
                          </EquipoCard>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </Container>
          );
        };

        export default ClasificacionEquipos;